// OSIS - Dashboard Principal Producție
import React, { useState } from 'react';
import { 
  Factory, 
  Package, 
  TrendingUp, 
  Users, 
  Calendar, 
  CheckCircle, 
  AlertTriangle,
  Home,
  BarChart,
  PlusCircle 
} from 'lucide-react';
import { mockData } from '../../utils/mockData';
import Card from '../../components/Card';

const ProductionDashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('săptămâna');

  // Statistici pentru OSIS
  const stats = [
    {
      title: 'Comenzi Active',
      value: '23',
      change: '+12%',
      changeType: 'positive',
      icon: Factory,
      color: 'primary'
    },
    {
      title: 'proiecte în Producție',
      value: '156',
      change: '+8%',
      changeType: 'positive', 
      icon: Package,
      color: 'secondary'
    },
    {
      title: 'Vânzări Luna',
      value: '42,580 RON',
      change: '+15%',
      changeType: 'positive',
      icon: TrendingUp,
      color: 'success'
    },
    {
      title: 'Echipa Activă',
      value: '10/11',
      change: 'Noemia în concediu',
      changeType: 'neutral',
      icon: Users,
      color: 'info'
    }
  ];

  const recentOrders = [
    { id: 'OSIS-024', client: 'SC Constructor SRL', products: '8x proiecte epoxidic', status: 'În producție', deadline: '2024-03-20' },
    { id: 'OSIS-025', client: 'Familia Popescu', products: '1x pardoseală heavy duty', status: 'Măsurători', deadline: '2024-03-22' },
    { id: 'OSIS-026', client: 'Casa Moderna SRL', products: '12x proiecte + 3x lucrări', status: 'Aprobare', deadline: '2024-03-25' },
    { id: 'OSIS-027', client: 'Familia Ionescu', products: '4x proiecte lemn', status: 'Planificare', deadline: '2024-03-28' }
  ];

  const productionQueue = [
    { product: 'proiect epoxidic 120x140', quantity: 8, client: 'SC Constructor', progress: 75, stage: 'Finisare' },
    { product: 'pardoseală heavy duty 90x200', quantity: 1, client: 'Familia Popescu', progress: 45, stage: 'Montaj materiale' },
    { product: 'proiect lemn 100x120', quantity: 4, client: 'Familia Ionescu', progress: 20, stage: 'Preparare' },
    { product: 'suprafață mare', quantity: 2, client: 'Vila Primaverii', progress: 10, stage: 'Măsurători' }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'În producție': return 'primary';
      case 'Măsurători': return 'warning';
      case 'Aprobare': return 'info';
      case 'Planificare': return 'secondary';
      default: return 'gray';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 75) return 'success';
    if (progress >= 50) return 'primary';
    if (progress >= 25) return 'warning';
    return 'gray';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Home className="w-8 h-8 mr-3 text-primary-600" />
            Dashboard OSIS
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Monitorizare producție pardoseli industriale - {new Date().toLocaleDateString('ro-RO', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <select 
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="form-select text-sm"
          >
            <option value="azi">Azi</option>
            <option value="săptămâna">Săptămâna</option>
            <option value="luna">Luna</option>
            <option value="trimestru">Trimestru</option>
          </select>
          
          <button className="btn btn-primary flex items-center">
            <PlusCircle className="w-4 h-4 mr-2" />
            Comandă Nouă
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="relative overflow-hidden">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
                  <p className={`text-sm mt-1 ${
                    stat.changeType === 'positive' ? 'text-success' :
                    stat.changeType === 'negative' ? 'text-error' : 
                    'text-gray-500 dark:text-gray-400'
                  }`}>
                    {stat.change}
                  </p>
                </div>
                <div className={`p-3 rounded-xl bg-${stat.color}-100 dark:bg-${stat.color}-900/30`}>
                  <Icon className={`w-6 h-6 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-primary-600" />
              Comenzi Recente
            </h3>
            <button className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              Vezi toate
            </button>
          </div>
          
          <div className="space-y-4">
            {recentOrders.map((order, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900 dark:text-white">{order.id}</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getStatusColor(order.status)}-100 text-${getStatusColor(order.status)}-700 dark:bg-${getStatusColor(order.status)}-900/30 dark:text-${getStatusColor(order.status)}-300`}>
                      {order.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{order.client}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{order.products}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">Termen: {order.deadline}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Production Queue */}
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Factory className="w-5 h-5 mr-2 text-secondary-600" />
              Coadă Producție
            </h3>
            <button className="text-secondary-600 hover:text-secondary-700 text-sm font-medium">
              Planificare
            </button>
          </div>
          
          <div className="space-y-4">
            {productionQueue.map((item, index) => (
              <div key={index} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900 dark:text-white">{item.product}</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">{item.quantity} buc</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{item.client}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-500">{item.stage}</span>
                  <span className={`text-xs font-medium text-${getProgressColor(item.progress)}-600`}>
                    {item.progress}%
                  </span>
                </div>
                <div className="mt-2 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className={`bg-${getProgressColor(item.progress)}-500 h-2 rounded-full transition-all duration-300`}
                    style={{ width: `${item.progress}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-accent-blue" />
          Acțiuni Rapide
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button className="p-4 text-left bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/30 dark:hover:bg-primary-900/50 rounded-xl transition-colors">
            <CheckCircle className="w-6 h-6 text-primary-600 mb-2" />
            <h4 className="font-medium text-gray-900 dark:text-white">Control Calitate</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Verificare produse finite</p>
          </button>
          
          <button className="p-4 text-left bg-secondary-50 hover:bg-secondary-100 dark:bg-secondary-900/30 dark:hover:bg-secondary-900/50 rounded-xl transition-colors">
            <Package className="w-6 h-6 text-secondary-600 mb-2" />
            <h4 className="font-medium text-gray-900 dark:text-white">Inventar Materiale</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Verificare stocuri</p>
          </button>
          
          <button className="p-4 text-left bg-warning-50 hover:bg-warning-100 dark:bg-warning-900/30 dark:hover:bg-warning-900/50 rounded-xl transition-colors">
            <AlertTriangle className="w-6 h-6 text-warning-600 mb-2" />
            <h4 className="font-medium text-gray-900 dark:text-white">Alertele Zilei</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">3 notificări importante</p>
          </button>
          
          <button className="p-4 text-left bg-success-50 hover:bg-success-100 dark:bg-success-900/30 dark:hover:bg-success-900/50 rounded-xl transition-colors">
            <TrendingUp className="w-6 h-6 text-success-600 mb-2" />
            <h4 className="font-medium text-gray-900 dark:text-white">Raport Săptămânal</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Performanța echipei</p>
          </button>
        </div>
      </Card>
    </div>
  );
};

export default ProductionDashboard;
